// @ts--check
import React from "react";
import { Box, Flex, Divider, HStack, IconButton, VStack } from "@chakra-ui/react";
import { IoArrowBackSharp, IoCloseSharp } from "react-icons/io5";

import LanguageSelector from "components/LanguageSelector";

/**
 * If defined, shows the back button and calls the method given.
 * @callback onBackButtonClick
 */

/**
 * Header
 * @description
 * Component displayed at the top of (for now) transaction history and transaction
 * details, allowing the end-user to close the widget and go back (if applicable).
 * @param {object} props
 * @param {string} props.title title - The title to show in the center of the header (required)
 * @param {string} [props.description] description - If set, displayed below the title
 * @param {onBackButtonClick} [props.onBackButtonClick] onBackButtonClick - If set, we display a back button in the top left corner
 * @param {OnComplete} [props.onCloseButtonClick] onCloseButtonClick - If set, we display a close button in the top right corner
 * @param {boolean} [props.showLanguage] Shows Language Selector dropdown
 * @returns JSX.Element
 */
const Header = ({ title, description, onBackButtonClick, onCloseButtonClick, showLanguage }) => {
  if (!title || title.length <= 0) {
    throw new Error("Header component requires a title");
  }

  return (
    <VStack
      data-testid="flow-header"
      position="sticky"
      top={0}
      pt={3}
      spacing={3}
      align="stretch"
      bg="background"
      zIndex={4}
      minH="68px"
    >
      <HStack flex={1} justify="space-between" px={4} spacing={0} role="heading" aria-label={title}>
        <Flex w="50px" justifyContent="flex-start">
          {onBackButtonClick ? (
            <IconButton
              variant="ghost"
              icon={<IoArrowBackSharp size="23px" />}
              colorScheme="gray"
              onClick={onBackButtonClick}
              aria-label="Go back"
              isRound
            />
          ) : null}
        </Flex>

        <VStack spacing={0} flex={1} align="center">
          <Box fontSize="md" fontWeight="bold" color="primaryText">
            {title}
          </Box>

          {description ? (
            <Box
              fontSize="xs"
              fontWeight="normal"
              color="secondaryText"
              role="contentinfo"
              aria-label={description}
            >
              {description}
            </Box>
          ) : null}
        </VStack>
        {showLanguage && <LanguageSelector hideLabel />}
        <Flex w="50px" justifyContent="flex-end">
          {onCloseButtonClick ? (
            <IconButton
              data-testid="header-x-button"
              variant="ghost"
              icon={<IoCloseSharp size="24px" />}
              colorScheme="gray"
              onClick={onCloseButtonClick}
              aria-label="Close"
              isRound
            />
          ) : null}
        </Flex>
      </HStack>

      <Divider />
    </VStack>
  );
};

export default Header;
